import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import threadsAPI from '../api/threads';
import sessionsAPI from '../api/sessions';
import './PersonalThreadView.css';

const PersonalThreadView = () => {
  const { userId } = useAuth();
  const [thread, setThread] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sessionsError, setSessionsError] = useState('');
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  const formatTimeAgo = (isoString) => {
    if (!isoString) return '';
    const now = new Date();
    const then = new Date(isoString);
    const seconds = Math.max(1, Math.floor((now.getTime() - then.getTime()) / 1000));

    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 5) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return `${years}y ago`;
  };

  useEffect(() => {
    const loadPersonalThread = async () => {
      if (!userId) return;
      setIsLoading(true);
      setError('');
      try {
        const data = await threadsAPI.getThreadsByOwner(userId);
        const threads = Array.isArray(data) ? data : [];
        const personalThread = threads.find((t) => t && t.is_personal === true);
        setThread(personalThread || null);
        if (!personalThread) {
          setError('No personal thread found');
        }
      } catch (err) {
        console.error('Failed to fetch personal thread:', err);
        setError('Failed to load personal thread');
      } finally {
        setIsLoading(false);
      }
    };

    loadPersonalThread();
  }, [userId]);

  useEffect(() => {
    const loadSessions = async () => {
      if (!thread?.id) return;
      setIsLoadingSessions(true);
      setSessionsError('');
      try {
        const data = await sessionsAPI.getSessionsByThread(thread.id);
        setSessions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        setSessionsError('Failed to load sessions');
      } finally {
        setIsLoadingSessions(false);
      }
    };

    loadSessions();
  }, [thread?.id]);

  return (
    <div className="content-area" style={{ paddingTop: 0 }}>
      {isLoading && <div className="loading-inline" style={{ marginTop: 12 }}>Loading personal thread...</div>}
      {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}

      {!isLoading && !error && thread && (
        <>
          <div style={{ margin: '0 0 16px 0' }} className="loading-inline">Personal thread sessions</div>
          {sessionsError && <div className="error-message">{sessionsError}</div>}
          {isLoadingSessions ? (
            <div className="loading-inline">Loading sessions...</div>
          ) : (
            <div className="threads-grid">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className="thread-card"
                  role="button"
                  tabIndex={0}
                >
                  <div className="thread-card-body">
                    <div className="thread-title">{session.label || 'Untitled session'}</div>
                    <div className="thread-subtitle">{formatTimeAgo(session.created_at)}</div>
                    {Array.isArray(session.users) && session.users.length > 0 && (
                      <div className="pill-row" aria-label="session users">
                        {session.users.map((u) => (
                          <span key={u.id} className="pill">
                            {u.username}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PersonalThreadView;
